import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	MarketStatus,
	MarketType,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@drift-labs/sdk';
import { matchEnum, OpenPosition } from '@drift/common';
import { useEffect, useMemo, useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { calculateMaxPositionSize } from 'src/utils/position';
import useCurrentPerpMarketAccount from '../../../hooks/useCurrentPerpMarketAccount';
import useWalletIsConnected from '../../../hooks/useWalletIsConnected';
import useUserWillExceedMaxPerpsPositions from 'src/hooks/useUserWillExceedMaxPerpsPositions';
import useOraclePrice from 'src/hooks/useOraclePrice';
import { TradeFormDerivedState } from 'src/@types/types';
import { getPriceInputLabel } from 'src/utils/trade';
import { useDebounce } from 'react-use';
import useMarketStateStore from 'src/stores/useMarketStateStore';
import { calculateMinTotalValue } from 'src/utils/scaledOrders';
import { valueIsBelowStepSize } from 'src/utils/math';
import { useCurrentMarketStepSize } from 'src/hooks/useMarketStepSize';
import { getMessageInfoForTradeFormState } from '../utils/getMessageInfoForTradeformState';
import { useMaxLeverageHandlers } from './useHandleIfMaxLeverageSelected';
import { useSyncPerpMaxLeverageAndFundsBreakdown } from './useSyncPerpMaxLeverageAndFundsBreakdown';
import { useSizeInputErrorMessage } from './useSizeInputErrorMessage';
import { useSyncOrderTypeControls } from './useSyncOrderTypeControls';
import { useSyncPriceBoxes } from './useSyncPriceBoxes';
import { useSyncIsFlippingPosition } from './useSyncIsFlippingPosition';
import { useSyncIsSwiftEnabled } from './useSyncIsSwiftEnabled';
import { useSyncGlobalSlippageTolerance } from './useSyncGlobalSlippageTolerance';
import { useSyncLeverageAfterTrade } from './useSyncLeverageAfterTrade';
import { usePersistAndHydrateLastUsedOrderType } from './usePersistAndHydrateLastUsedOrderType';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import usePreviewOrder from './usePreviewOrder';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useModal from 'src/components/Modals/hooks/useModal';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';
import { useL2Orderbook } from 'src/hooks/query/orderbook/useL2Orderbook';

const defaultTradeFormDerivedState: TradeFormDerivedState = {
	priceBoxHeading: '',
	priceBoxEnabled: false,
	showSlippageTolerance: false,
	showOrdersSplit: false,
	shouldShowMessage: false,
	messageType: 'info',
	messageTitle: '',
	message: '',
	skipMessageInConfirmationModal: false,
	showSecondaryPriceBox: false,
	isReduceOnlyDisabled: false,
	showReduceOnly: false,
	showPostOnly: false,
	showLeverageSlider: false,
	tradeButtonIsDisabled: false,
	currentPosition: {
		marketIndex: 0,
		direction: 'long',
		baseSize: ZERO,
		entryPrice: ZERO,
	} as OpenPosition,
	maxQuoteAvailable: 0,
	maxBaseAvailable: 0,
	showReducePositionSlider: false,
	maxLeverageAvailable: 0,
	showPriceEstimateOracleDivergenceWarning: false,
	handleMaxLeverageClick: () => {},
	handleFormChange: () => {},
	stepSize: 0,
	sizeErrorMessage: undefined,
	positionSizeLimitExceeded: undefined,
	displayLeverage: 0,
};

/**
 * Hook that derives order information from the trade form inputs
 * WARNING: do not call this hook multiple times or you will see weird behavior with order type and other localStorage effects and looping
 */
export const usePerpTradeFormStateEngine = (): TradeFormDerivedState => {
	// Generic App Hooks
	const setState = useDriftStore((s) => s.set);
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
	const tradeForm = useDriftStore((s) => s.tradeForm);
	const currentOrderType = useDriftStore((s) => s.tradeForm.orderType);
	const connected = useWalletIsConnected();
	const currentPerpMarket = useCurrentPerpMarketAccount();
	const { show } = useModal();

	// Selected Market
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const stepSize = useCurrentMarketStepSize();

	const areOrderbooksLoaded = useMarketStateStore(
		(s) => s.latestUpdateSlot !== 0
	);

	// Trade Form
	const quoteSize = Number(tradeForm.quoteSizeStringValue);
	const priceBoxValue = Number(tradeForm.priceBoxStringValue);

	// Oracle and Market Prices
	const previewPriceImpact = usePreviewOrder().priceImpact;
	const oraclePrice = useOraclePrice(selectedMarket?.marketId);
	const currentMarketPriceBigNum =
		BigNum.from(previewPriceImpact?.markPrice, QUOTE_PRECISION_EXP) ??
		BigNum.zero(QUOTE_PRECISION_EXP);
	const currentMarketPrice = parseFloat(currentMarketPriceBigNum.print());

	const exceedsLiquidity = previewPriceImpact?.exceedsLiquidity ?? false;

	const userWillExceedMaxPerpsPositions = useUserWillExceedMaxPerpsPositions(
		selectedMarket?.market.marketIndex
	);

	// Current User Account Data
	const currentUserOpenPerpPositions = useCurrentAccountPositions();
	const marginInfo = useCurrentAccountMarginInfo();
	const currentPosition = useMemo(
		() =>
			currentUserOpenPerpPositions?.find(
				(pos) => pos.marketIndex === selectedMarket?.marketIndex
			),
		[currentUserOpenPerpPositions, selectedMarket?.marketIndex]
	);
	const userHasPositionInMarket = currentPosition !== undefined;
	const currentAccountName = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.name
	);
	const currentUserClient = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.client
	);
	const currentAccountFreeCollateral = marginInfo?.freeCollateral;

	const currentPositionIsZero =
		!currentPosition || currentPosition.baseSize.eq(ZERO);
	const currentPositionDirection = currentPosition?.direction ?? 'long'; // for logical purposes position is "long" if position is zero

	const marketInSettlement = matchEnum(
		currentPerpMarket?.status,
		MarketStatus.SETTLEMENT
	);
	const isMobileScreenSize = useIsMobileScreenSize();

	const priceInputLabel = getPriceInputLabel(currentOrderType);

	// Price Box Enabled
	const priceBoxEnabled = (() => {
		switch (currentOrderType) {
			case 'market':
				return false;
			case 'oracle':
				return false;
			default:
				return true;
		}
	})();

	const baseSizeInputBN = useMemo(
		() =>
			BigNum.fromPrint(tradeForm.baseSizeStringValue, BASE_PRECISION_EXP).val,
		[tradeForm.baseSizeStringValue]
	);

	// Order Type Controls
	const {
		isReduceOnlyDisabled,
		showPostOnlyControl,
		showReducePositionSlider,
		showLeverageSlider,
		showSecondaryPriceBox,
		showSlippageTolerance,
		showOrdersSplit,
		showPriceEstimateOracleDivergenceWarning,
	} = useSyncOrderTypeControls(
		currentOrderType,
		currentPositionDirection,
		currentPosition,
		baseSizeInputBN,
		tradeForm.side,
		tradeForm.reduceOnly
	);

	// Max Leverage and Funds Breakdown
	const { maxLeverageAvailable, maxBaseAvailable, maxQuoteAvailable } =
		useSyncPerpMaxLeverageAndFundsBreakdown({
			tradeData: tradeForm,
			selectedMarket,
			perpMarketAccount: currentPerpMarket,
			currentUserClient,
			currentPosition,
			currentPositionDirection,
			currentAccountFreeCollateral,
		});
	const { handleMaxLeverageClick, handleFormChange } = useMaxLeverageHandlers();

	// Syncs
	useSyncPriceBoxes(
		tradeForm.side,
		tradeForm.priceBoxStringValue,
		tradeForm.secondaryPriceBoxStringValue,
		currentOrderType,
		selectedMarket
	);

	useSyncIsFlippingPosition(
		tradeForm.side,
		currentPositionDirection,
		currentPositionIsZero
	);

	useSyncIsSwiftEnabled(selectedMarket, currentOrderType, tradeForm.postOnly);

	useSyncGlobalSlippageTolerance();

	useSyncLeverageAfterTrade(
		tradeForm.side,
		quoteSize,
		selectedMarket?.marketId
	);

	usePersistAndHydrateLastUsedOrderType(currentOrderType);

	const minTradeSize = currentPerpMarket
		? Math.max(
				BigNum.from(
					currentPerpMarket.amm.minOrderSize,
					BASE_PRECISION_EXP
				).toNum(),
				currentOrderType === 'scaledOrders'
					? calculateMinTotalValue(
							BigNum.from(
								currentPerpMarket.amm.minOrderSize,
								BASE_PRECISION_EXP
							),
							tradeForm.scaledOrders.orderCount
						).toNum()
					: 0
			)
		: undefined;

	const openAddAccountModal = () => {
		show({ type: 'new_subaccount', props: {} });
	};

	// L2 state for est entry price to calculate max base size
	const { data: l2Orderbook } = useL2Orderbook(
		selectedMarket?.marketId ?? null
	);
	const l2State = l2Orderbook ?? { asks: [], bids: [] };

	const {
		messageType,
		shouldShowMessage,
		message,
		skipMessageInConfirmationModal,
		messageLink,
		messageLinkDescription,
		messageTitle,
		disableTradeButton,
		messageId,
		messagePosition,
	} = getMessageInfoForTradeFormState(
		currentOrderType,
		maxQuoteAvailable,
		stepSize,
		tradeForm,
		priceBoxValue,
		currentMarketPrice,
		userHasPositionInMarket,
		isGeoblocked,
		selectedMarket?.baseAssetSymbol(),
		exceedsLiquidity,
		MarketType.PERP,
		openAddAccountModal,
		false,
		userWillExceedMaxPerpsPositions,
		oraclePrice ? oraclePrice : BigNum.zero(QUOTE_PRECISION_EXP),
		l2State,
		areOrderbooksLoaded,
		currentPositionDirection,
		undefined,
		undefined,
		currentAccountName,
		undefined,
		currentPerpMarket?.status,
		currentUserOpenPerpPositions,
		selectedMarket?.marketIndex,
		isMobileScreenSize
	);

	const sizeErrorMessage = useSizeInputErrorMessage({
		baseSizeStringValue: tradeForm.baseSizeStringValue,
		stepSize: stepSize,
		maxBaseAvailable,
		minTradeSizeForMarket: minTradeSize ?? 1,
		quoteSizeStringValue: tradeForm.quoteSizeStringValue,
		marketSymbol: selectedMarket?.baseAssetSymbol(),
	});

	const displayLeverage = useGetDisplayLeverageForMarket(
		selectedMarket?.marketIndex
	);

	// Check if the position size would exceed the maximum allowed for the current leverage
	const positionSizeLimitExceeded = useMemo(() => {
		if (!currentPerpMarket || !displayLeverage || !selectedMarket) {
			return;
		}

		const maxPositionSize = calculateMaxPositionSize(
			displayLeverage,
			currentPerpMarket
		);

		const proposedTradeSize = BigNum.fromPrint(
			tradeForm.baseSizeStringValue || '0',
			BASE_PRECISION_EXP
		).val;

		// Calculate the final position size after the trade
		const currentPositionSize = currentPosition?.baseSize?.abs() ?? ZERO;
		const tradeSide = tradeForm.side; // 'buy' or 'sell'
		const isIncreasingPosition =
			(tradeSide === 'buy' && currentPositionDirection === 'long') ||
			(tradeSide === 'sell' && currentPositionDirection === 'short') ||
			currentPositionIsZero;

		let finalPositionSize: BN;
		if (isIncreasingPosition) {
			// Adding to position or opening new position
			finalPositionSize = currentPositionSize.add(proposedTradeSize);
		} else {
			// Reducing position or flipping - take absolute value of the result
			finalPositionSize = currentPositionSize.sub(proposedTradeSize).abs();
		}

		const wouldExceedLimit = finalPositionSize.gt(maxPositionSize);

		if (wouldExceedLimit) {
			return {
				messageTitle: 'Position Size Limit Exceeded',
				shouldShowMessage: true,
				messageType: 'warning' as const,
				message: `You are exceeding the maximum allowable position size of ${BigNum.from(
					maxPositionSize,
					BASE_PRECISION_EXP
				).prettyPrint(
					true
				)} ${selectedMarket.baseAssetSymbol()} at the current leverage level.`,
				skipMessageInConfirmationModal: false,
				disableTradeButton: true,
			};
		}

		return undefined;
	}, [
		currentPerpMarket?.marketIndex,
		displayLeverage,
		tradeForm.baseSizeStringValue,
		tradeForm.side,
		currentPosition?.baseSize,
		currentPositionDirection,
		currentPositionIsZero,
		selectedMarket?.baseAssetSymbol,
	]);

	const [debouncedShouldShowMessage, setDebouncedShouldShowMessage] =
		useState(false);

	// we only want to debounce the message if it is showing
	useDebounce(
		() => {
			setDebouncedShouldShowMessage(shouldShowMessage);
		},
		500,
		[shouldShowMessage]
	);
	useEffect(() => {
		if (!shouldShowMessage) {
			setDebouncedShouldShowMessage(false);
		}
	}, [shouldShowMessage]);

	// sync price impact from preview order params for anywhere else that needs it
	useEffect(() => {
		if (previewPriceImpact) {
			setState((s) => {
				s.tradeForm.priceImpact = previewPriceImpact;
			});
		}
	}, [
		previewPriceImpact?.marketIndex,
		previewPriceImpact?.marketType,
		previewPriceImpact?.entryPrice,
		previewPriceImpact?.priceImpactInputBaseSize,
	]);

	const tradeButtonIsDisabled =
		valueIsBelowStepSize(tradeForm.baseSizeStringValue, stepSize) ||
		marketInSettlement ||
		disableTradeButton ||
		sizeErrorMessage?.disableTradeButton ||
		positionSizeLimitExceeded?.disableTradeButton ||
		!tradeForm.baseSizeStringValue ||
		!connected ||
		!quoteSize ||
		(priceBoxEnabled &&
			(currentOrderType === 'oracleLimit'
				? !tradeForm.priceBoxStringValue
				: !Number(tradeForm.priceBoxStringValue)));

	return {
		// component display states
		priceBoxHeading: priceInputLabel,
		priceBoxEnabled,
		showReduceOnly: true,
		isReduceOnlyDisabled,
		showPostOnly: showPostOnlyControl,
		showReducePositionSlider: showReducePositionSlider ?? false,
		showLeverageSlider,
		showSecondaryPriceBox,
		showSlippageTolerance,
		showOrdersSplit,
		showPriceEstimateOracleDivergenceWarning,
		tradeButtonIsDisabled,

		// message states
		shouldShowMessage: debouncedShouldShowMessage,
		messageType,
		message,
		skipMessageInConfirmationModal,
		currentPosition:
			currentPosition ?? defaultTradeFormDerivedState.currentPosition,
		messageLink,
		messageLinkDescription,
		messageTitle,
		messageId,
		messagePosition,
		sizeErrorMessage,
		positionSizeLimitExceeded,

		// max leverage
		maxQuoteAvailable,
		maxBaseAvailable,
		maxLeverageAvailable: maxLeverageAvailable ?? 0,
		handleMaxLeverageClick,
		handleFormChange,

		// market states
		marketInSettlement,
		stepSize,
		displayLeverage,
	};
};
